// src/componants/common/SeidBar.js
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { useIndustryLabels } from "../../context/IndustryContext";
import { useNavigation } from "../../context/NavigationContext";
import { isNavItemVisible } from "../../utils/featureVisibility";
import { getStructureNavLabel, isModeratorTimesheetLeaderUser } from "../../utils/orgHelpers";
import {
  isHrFamilyRole,
  canAccessZktPayroll,
  canManageApprovalCycles,
} from "../../utils/hrStaffHelpers";
import {
  canSeeWfhNav,
  canUseRemoteAttendance,
} from "../../utils/workModeHelpers";
import { canRouteAdvanceDisbursement } from "../../utils/advanceDisbursement";
import { canRouteCashEntitlementDisbursement } from "../../utils/cashEntitlement";
import {
  applyIndustryNavLabels,
  groupNavItems,
  NAV_ITEMS_BY_ROLE,
} from "../../utils/navConfig";
import { useMyWorkMode } from "../../hooks/useMyWorkMode";
import {
  LayoutGrid,
  FileText,
  Landmark,
  MessageSquareWarning,
  Bell,
  LogOut,
  Users,
  ShieldCheck,
  Settings,
  AlertTriangle,
  Inbox,
  Upload,
  CalendarDays,
  Wallet,
  Banknote,
  Clock,
  Package,
  ClipboardCheck,
  BarChart3,
  Megaphone,
  UserPlus,
  UserCircle,
  Scale,
  Shield,
  BookOpen,
  BookMarked,
  X,
  Timer,
  Home,
  Bus,
  ChevronDown,
  Search,
} from "lucide-react";
import BrandIdentity from "./BrandIdentity";
import ThemeToggle from "./ThemeToggle";
import { isBrandAdminUiEnabled } from "../../config/brandAdminAccess";
import { DEVELOPER_CREDIT } from "../../config/developer";

const iconMap = {
  LayoutGrid,
  FileText,
  Landmark,
  MessageSquareWarning,
  Bell,
  LogOut,
  Users,
  ShieldCheck,
  Settings,
  AlertTriangle,
  Inbox,
  Upload,
  CalendarDays,
  Wallet,
  Banknote,
  Clock,
  Package,
  ClipboardCheck,
  BarChart3,
  Megaphone,
  UserPlus,
  UserCircle,
  Scale,
  Shield,
  BookOpen,
  BookMarked,
  Timer,
  Home,
  Bus,
};

const REMOTE_NAV_IDS = new Set(["attendance", "wfh"]);

const navItemsByRole = NAV_ITEMS_BY_ROLE;

const styles = `
.ess-sidebar {
  width: 256px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--brand-primary);
  font-family: sans-serif;
  direction: rtl;
}

.ess-sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid var(--brand-secondary);
}

.ess-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: #D0E7E6;
  color: var(--brand-primary);
}

.ess-sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
}

.ess-sidebar-logo-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
}

.ess-sidebar-brand-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ess-sidebar-app-name {
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.ess-sidebar-slogan {
  font-size: 11px;
  font-weight: 500;
  color: rgba(208, 231, 230, 0.88);
  line-height: 1.35;
}

.ess-user-info {
  padding: 12px 16px;
  border-bottom: 1px solid var(--brand-secondary);
}

.ess-user-name {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.ess-user-role {
  font-size: 11px;
  color: #95CCDD;
  margin-top: 2px;
}

.ess-nav {
  flex: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.ess-nav-search-wrap {
  position: relative;
  margin-bottom: 8px;
}

.ess-nav-search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #95CCDD;
  pointer-events: none;
}

.ess-nav-search {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 34px 10px 12px;
  border: 1px solid var(--brand-secondary);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 13px;
  font-family: inherit;
}

.ess-nav-search::placeholder {
  color: rgba(208, 231, 230, 0.7);
}

.ess-nav-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
}

.ess-nav-section-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  color: #95CCDD;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  font-family: inherit;
}

.ess-nav-section-toggle svg {
  transition: transform 0.15s ease;
}

.ess-nav-section-toggle.is-collapsed svg {
  transform: rotate(180deg);
}

.ess-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: right;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #D0E7E6;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.ess-nav-item:hover:not(.is-active) {
  background-color: var(--brand-secondary);
}

.ess-nav-item.is-active {
  background-color: #fff;
  color: var(--brand-primary);
  font-weight: 600;
}

.ess-nav-icon {
  color: #95CCDD;
}

.ess-nav-item.is-active .ess-nav-icon {
  color: var(--brand-primary);
}

.ess-footer {
  padding: 16px;
  border-top: 1px solid var(--brand-secondary);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ess-developer-credit {
  margin: 4px 0 0;
  text-align: center;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.2px;
  color: rgba(255, 255, 255, 0.5);
}

.ess-logout-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #D0E7E6;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ess-logout-btn:hover {
  background-color: #c0392b;
  color: #fff;
}

.ess-logout-btn:hover .ess-nav-icon {
  color: #fff;
}

.ess-nav-item-wrapper {
  position: relative;
  width: 100%;
}

.ess-nav-badge {
  position: absolute;
  top: 6px;
  left: 10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background-color: #e74c3c;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  pointer-events: none;
}

.ess-sidebar-close-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: #d0e7e6;
  cursor: pointer;
  flex-shrink: 0;
}

.ess-sidebar-close-btn:active {
  background-color: rgba(255, 255, 255, 0.2);
}

@media (max-width: 768px) {
  .ess-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    right: auto;
    z-index: 1100;
    transform: translateX(-100%);
    transition: transform 0.28s ease;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
    padding-left: env(safe-area-inset-left, 0px);
  }

  .ess-sidebar.is-open {
    transform: translateX(0);
  }

  .ess-sidebar-close-btn {
    display: flex;
  }

  .ess-nav-item {
    padding: 14px 16px;
    min-height: 48px;
  }

  .ess-logout-btn {
    min-height: 48px;
  }
}

@media (min-width: 769px) {
  .ess-sidebar {
    transform: none !important;
    flex-shrink: 0;
  }
}

.ess-logout-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.ess-logout-confirm-modal {
  background: #fff;
  border: 1px solid #95ccdd;
  border-radius: 8px;
  padding: 24px 28px;
  max-width: 400px;
  width: 100%;
  direction: rtl;
  box-shadow: 0 12px 40px rgba(41, 54, 129, 0.18);
}

.ess-logout-confirm-title {
  margin: 0 0 8px;
  color: var(--brand-primary);
  font-size: 16px;
  font-weight: 700;
}

.ess-logout-confirm-subtitle {
  margin: 0 0 22px;
  color: var(--brand-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.ess-logout-confirm-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.ess-logout-confirm-cancel {
  background-color: transparent;
  color: var(--brand-primary);
  border: 1px solid #95ccdd;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
}

.ess-logout-confirm-cancel:hover {
  background-color: #d0e7e6;
}

.ess-logout-confirm-ok {
  background-color: #c0392b;
  color: #fff;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
}

.ess-logout-confirm-ok:hover {
  background-color: #a93226;
}
`;

function SeidBar({
  activeId,
  onSelect,
  userName,
  userRole,
  userDepartmentId,
  userJobTitle,
  onLogout,
  badges = {},
  mobileOpen = false,
  onClose,
  frozen = false,
  profileLocked = false,
}) {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [navQuery, setNavQuery] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});
  const { user } = useAuth();
  const brand = useBrand();
  const flags = useFeatureFlags();
  const industryLabels = useIndustryLabels();
  const navigation = useNavigation();
  const { workMode } = useMyWorkMode();

  const lockNav = frozen || profileLocked;
  const baseNavItems = frozen
    ? []
    : profileLocked
      ? [{ id: "my-profile", label: "ملفي الشخصي", icon: "UserCircle" }]
      : (() => {
        if (isHrFamilyRole(userRole)) {
          let items = navItemsByRole.hr || [];
          if (!canAccessZktPayroll(userRole)) {
            items = items.filter(
              (item) => item.id !== "kmt-payroll" && item.id !== "sales-targets",
            );
          }
          if (!canManageApprovalCycles(userRole)) {
            items = items.filter((item) => item.id !== "approval-cycles");
          }
          return items;
        }
        return navItemsByRole[userRole] || navItemsByRole.employee;
      })();
  const structureLabel = getStructureNavLabel(userRole, userDepartmentId);
  const routeUser = {
    role: userRole,
    jobTitle: userJobTitle,
    departmentId: userDepartmentId,
    departmentCode: user?.departmentCode,
  };
  const canRouteAdvance = canRouteAdvanceDisbursement(routeUser);
  const canRouteCash = canRouteCashEntitlementDisbursement(routeUser);
  // تأكيد الصرف للمكلَّف فقط: مسؤول الخزنة أو الكاشير (بعد توجيه المحاسب العام)
  const canConfirmDisbursement =
    userJobTitle === "مسؤول الخزنة" || userJobTitle === "كاشير";
  const canViewDisbursementLedger =
    isHrFamilyRole(userRole) ||
    canRouteAdvance ||
    (userRole === "department-manager" && userDepartmentId === "accounts");
  // حضور التطبيق: REMOTE + HYBRID فقط
  // العمل عن بُعد (طلب): HYBRID فقط؛ المديرون/HR للموافقة حتى لو OFFICE
  const showRemoteAttendance = canUseRemoteAttendance(workMode);
  const showWfh = canSeeWfhNav(
    workMode,
    userRole,
    user?.backendRoles || [],
  );

  let navItems = applyIndustryNavLabels(
    (profileLocked
      ? baseNavItems
      : baseNavItems.filter((item) => {
        if (item.id === "brands-admin" && !isBrandAdminUiEnabled()) return false;
        if (!isNavItemVisible(flags, item.id)) return false;
        if (!REMOTE_NAV_IDS.has(item.id)) return true;
        if (item.id === "attendance") return showRemoteAttendance;
        if (item.id === "wfh") return showWfh;
        return true;
      })
    ).map((item) =>
      item.id === "departments"
        ? { ...item, label: structureLabel }
        : item,
    ),
    industryLabels,
  );

  if (canRouteAdvance && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "advance-disbursement",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "advance-approvals" ||
          item.id === "advance" ||
          item.id === "approvals",
      );
      const disbursementItem = {
        id: "advance-disbursement",
        label: "توجيه صرف السلف",
        icon: "Wallet",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          disbursementItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, disbursementItem];
      }
    }
  }

  if (canConfirmDisbursement && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "advance-confirm",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "advance-disbursement" ||
          item.id === "advance" ||
          item.id === "approvals",
      );
      const confirmItem = {
        id: "advance-confirm",
        label: "تأكيد صرف السلف",
        icon: "Banknote",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          confirmItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, confirmItem];
      }
    }
  }

  if (canViewDisbursementLedger && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "advance-ledger",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "advance-approvals" ||
          item.id === "advance-disbursement" ||
          item.id === "advance" ||
          item.id === "approvals",
      );
      const ledgerItem = {
        id: "advance-ledger",
        label: "سجل صرف السلف",
        icon: "Landmark",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          ledgerItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, ledgerItem];
      }
    }
  }

  if (canRouteCash && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "cash-entitlement-disbursement",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "cash-entitlement-create" ||
          item.id === "advance-ledger" ||
          item.id === "advance-disbursement",
      );
      const disbursementItem = {
        id: "cash-entitlement-disbursement",
        label: "توجيه المستحقات النقدية",
        icon: "Wallet",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          disbursementItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, disbursementItem];
      }
    }
  }

  if (canConfirmDisbursement && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "cash-entitlement-confirm",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "cash-entitlement-disbursement" ||
          item.id === "advance-confirm",
      );
      const confirmItem = {
        id: "cash-entitlement-confirm",
        label: "تأكيد صرف المستحقات",
        icon: "Banknote",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          confirmItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, confirmItem];
      }
    }
  }

  if (canViewDisbursementLedger && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "cash-entitlement-ledger",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "cash-entitlement-confirm" ||
          item.id === "cash-entitlement-disbursement" ||
          item.id === "advance-ledger",
      );
      const ledgerItem = {
        id: "cash-entitlement-ledger",
        label: "سجل المستحقات النقدية",
        icon: "Landmark",
        section: "pay",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          ledgerItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, ledgerItem];
      }
    }
  }

  const isInternalControlMember =
    userDepartmentId === "internal-control" ||
    user?.departmentCode === "IC" ||
    /الرقاب[ةه]\s*الداخل/u.test(String(user?.department || ""));
  if (isInternalControlMember && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "branch-timesheet",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "attendance" ||
          item.id === "my-days" ||
          item.id === "wfh",
      );
      const timesheetItem = {
        id: "branch-timesheet",
        label: "الخطط الأسبوعية",
        icon: "CalendarDays",
        section: "attendance",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          timesheetItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, timesheetItem];
      }
    }
  }

  if (isModeratorTimesheetLeaderUser(user) && !lockNav) {
    const alreadyPresent = navItems.some(
      (item) => item.id === "branch-timesheet",
    );
    if (!alreadyPresent) {
      const insertAfter = navItems.findIndex(
        (item) =>
          item.id === "attendance" ||
          item.id === "my-days" ||
          item.id === "wfh",
      );
      const timesheetItem = {
        id: "branch-timesheet",
        label: "خطة الموديريتور",
        icon: "CalendarDays",
        section: "attendance",
      };
      if (insertAfter >= 0) {
        navItems = [
          ...navItems.slice(0, insertAfter + 1),
          timesheetItem,
          ...navItems.slice(insertAfter + 1),
        ];
      } else {
        navItems = [...navItems, timesheetItem];
      }
    }
  }

  const roleLabels = {
    employee: "موظف",
    manager: "مدير",
    "department-manager": "مدير إدارة",
    hr: "موارد بشرية",
    "hr-staff": "موارد بشرية (موظف)",
    gm: "المدير العام",
    owner: "مالك الشركة",
    admin: "أدمن النظام",
  };

  function handleConfirmLogout() {
    setShowLogoutConfirm(false);
    onLogout?.();
  }

  const query = navQuery.trim();
  const filteredNavItems = query
    ? navItems.filter((entry) => String(entry.label || "").includes(query))
    : navItems;
  const navGroups = groupNavItems(filteredNavItems);
  const showNavSearch = !lockNav && navItems.length >= 12;

  function toggleSection(sectionId) {
    if (!sectionId) return;
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }

  function renderNavItem(item) {
    const Icon = iconMap[item.icon] || LayoutGrid;
    const isActive =
      activeId === item.id ||
      (item.id === "reports" && activeId === "attendance-summary");
    const badgeCount = badges[item.id] || 0;
    return (
      <div key={item.id} className="ess-nav-item-wrapper">
        <button
          type="button"
          onClick={() => {
            if (navigation?.navigate) navigation.navigate(item.id);
            else onSelect(item.id);
          }}
          className={`ess-nav-item ${isActive ? "is-active" : ""}`}
        >
          <Icon size={18} className="ess-nav-icon" />
          <span>{item.label}</span>
        </button>
        {badgeCount > 0 && (
          <span className="ess-nav-badge">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className={`ess-sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="ess-sidebar-header">
          <span className="ess-sidebar-brand">
            <BrandIdentity
              showName={false}
              logoClassName="ess-sidebar-logo-img"
              fallbackClassName="ess-badge"
            />
            <span className="ess-sidebar-brand-text">
              <span className="ess-sidebar-app-name" dir="ltr" lang="en">
                {brand.appName || brand.companyName || brand.shortName}
              </span>
            </span>
          </span>
          <button
            type="button"
            className="ess-sidebar-close-btn"
            onClick={onClose}
            aria-label="إغلاق القائمة"
          >
            <X size={20} />
          </button>
        </div>

        <div className="ess-user-info">
          <div className="ess-user-name">{userName || "غير معروف"}</div>
          <div className="ess-user-role">
            {roleLabels[userRole] || userRole}
          </div>
          {frozen && (
            <div className="ess-user-role" style={{ marginTop: 8, color: "#f5c542" }}>
              حساب مجمّد — استقالة معلّقة
            </div>
          )}
          {profileLocked && (
            <div className="ess-user-role" style={{ marginTop: 8, color: "#f5c542" }}>
              أكمل بياناتك الشخصية للمتابعة
            </div>
          )}
        </div>

        <nav className="ess-nav">
          {showNavSearch ? (
            <div className="ess-nav-search-wrap">
              <Search size={14} className="ess-nav-search-icon" aria-hidden="true" />
              <input
                type="search"
                className="ess-nav-search"
                value={navQuery}
                onChange={(event) => setNavQuery(event.target.value)}
                placeholder="بحث في القائمة"
                aria-label="بحث في القائمة"
              />
            </div>
          ) : null}
          {navGroups.map((group) => {
            const collapsed =
              Boolean(group.id) &&
              !navQuery.trim() &&
              collapsedSections[group.id];
            return (
              <div key={group.id || "flat"} className="ess-nav-section">
                {group.label ? (
                  <button
                    type="button"
                    className={`ess-nav-section-toggle ${collapsed ? "is-collapsed" : ""}`}
                    onClick={() => toggleSection(group.id)}
                    aria-expanded={!collapsed}
                  >
                    <span>{group.label}</span>
                    <ChevronDown size={14} />
                  </button>
                ) : null}
                {collapsed ? null : group.items.map(renderNavItem)}
              </div>
            );
          })}
        </nav>

        <div className="ess-footer">
          <ThemeToggle variant="chrome" />
          <button
            type="button"
            className="ess-logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
          >
            <LogOut size={18} className="ess-nav-icon" />
            <span>تسجيل الخروج</span>
          </button>
          <p className="ess-developer-credit" dir="ltr" lang="en">
            {DEVELOPER_CREDIT}
          </p>
        </div>
      </div>

      {showLogoutConfirm && (
        <div
          className="ess-logout-confirm-overlay"
          onClick={() => setShowLogoutConfirm(false)}
          role="presentation"
        >
          <div
            className="ess-logout-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ess-logout-confirm-title"
          >
            <h3
              id="ess-logout-confirm-title"
              className="ess-logout-confirm-title"
            >
              تأكيد تسجيل الخروج
            </h3>
            <p className="ess-logout-confirm-subtitle">
              هل أنت متأكد أنك تريد تسجيل الخروج من الحساب؟
            </p>
            <div className="ess-logout-confirm-buttons">
              <button
                type="button"
                className="ess-logout-confirm-cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="ess-logout-confirm-ok"
                onClick={handleConfirmLogout}
              >
                تسجيل الخروج
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SeidBar;
